import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { radius } from "src/design/tokens";

export interface FilterPillOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterPillsProps {
  options: FilterPillOption[];
  value: string;
  onChange: (value: string) => void;
  "aria-label": string;
  /** Stable id for the e2e suite, on the group. */
  "data-testid"?: string;
  size?: "sm" | "md";
  /**
   * `plain` (default): loose pills on the page. `tray`: the design's pill tray, a surface
   * (or surfaceAlt) container with 4 px padding that the pills sit inside.
   */
  variant?: "plain" | "tray" | "trayAlt";
}

/** Single-select pill group. Pills: surfaceAlt 700/13; selected: strong/onStrong 800 (theme ToggleButton override). */
export const FilterPills = ({
  options,
  value,
  onChange,
  size = "md",
  variant = "plain",
  "aria-label": ariaLabel,
  "data-testid": testId,
}: FilterPillsProps) => (
  <ToggleButtonGroup
    exclusive
    value={value}
    aria-label={ariaLabel}
    data-testid={testId}
    onChange={(_event, next: string | null) => {
      if (next !== null) onChange(next);
    }}
    sx={(theme) =>
      variant === "plain"
        ? { gap: "6px" }
        : {
            gap: "4px",
            p: "4px",
            borderRadius: `${radius.pill}px`,
            bgcolor:
              variant === "tray"
                ? theme.palette.background.paper
                : theme.palette.surfaceAlt,
            boxShadow: variant === "tray" ? theme.shadows[1] : "none",
            "& .MuiToggleButton-root:not(.Mui-selected)": {
              bgcolor: "transparent",
            },
          }
    }
  >
    {options.map((option) => (
      <ToggleButton
        key={option.value}
        value={option.value}
        sx={(theme) => ({
          minHeight: size === "sm" ? 32 : 36,
          px: size === "sm" ? 1.5 : 1.75,
          py: 0,
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.palette.focusRing}`,
            outlineOffset: 2,
          },
        })}
      >
        {option.label}
        {option.count !== undefined ? (
          <Box component="span" sx={{ ml: 0.75, opacity: 0.8 }}>
            {option.count}
          </Box>
        ) : null}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export default FilterPills;
